import React from "react";
import { Callout } from "./Callout";
import { Image } from "./Image";
import { YouTube } from "./YouTube";

type MarkdownComponents = {
  [key: string]: React.ComponentType<any>;
};

function ytId(href: string): string | null {
  const m = href?.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([a-zA-Z0-9_-]+)/);
  return m ? m[1] : null;
}

// Pull plain text out of a hast node's children
function hastText(node: any): string {
  if (!node?.children) return "";
  return node.children
    .map((c: any) => (c.type === "text" ? (c.value as string) : hastText(c)))
    .join("");
}

export function getMarkdownComponents(onPageClick?: (pageId: string) => void): MarkdownComponents {
  return {
    h1: ({ ...props }) => (
      <h1 className="text-base font-semibold mb-1 mt-3 first:mt-0" {...props} />
    ),
    h2: ({ ...props }) => (
      <h2 className="text-sm font-semibold mb-1 mt-3 border-b border-border pb-1" {...props} />
    ),
    h3: ({ ...props }) => <h3 className="text-xs font-semibold mb-1 mt-2" {...props} />,
    h4: ({ ...props }) => <h4 className="text-xs font-semibold mb-1 mt-1.5" {...props} />,
    h5: ({ ...props }) => <h5 className="text-xs font-semibold mb-1 mt-1.5" {...props} />,
    h6: ({ ...props }) => <h6 className="text-xs font-semibold mb-1 mt-1.5" {...props} />,
    p: ({ children, node: _node, ...props }: any) => {
      // Detect single-child block elements that can't live inside <p>
      const childArray = React.Children.toArray(children);
      if (childArray.length === 1) {
        const child = childArray[0] as React.ReactElement<any>;
        // YouTube link
        const href = child?.props?.href;
        if (href) {
          const id = ytId(href);
          if (id) return <YouTube id={id} title={String(child.props.children)} />;
        }
        // Standalone image
        const src = child?.props?.src;
        if (src !== undefined) {
          return <Image src={src} alt={child.props.alt ?? ""} />;
        }
      }
      return (
        <p className="my-1 leading-5" {...props}>
          {children}
        </p>
      );
    },
    ul: ({ ...props }) => <ul className="my-1.5 ml-4 list-disc space-y-0.5" {...props} />,
    ol: ({ ...props }) => <ol className="my-1.5 ml-4 list-decimal space-y-0.5" {...props} />,
    li: ({ ...props }) => <li className="leading-5" {...props} />,
    hr: ({ ...props }) => <hr className="my-3 border-border" {...props} />,
    strong: ({ ...props }) => <strong className="font-semibold" {...props} />,
    em: ({ ...props }) => <em className="italic" {...props} />,
    table: ({ ...props }) => (
      <div className="my-2 w-full overflow-x-auto">
        <table className="w-full border-collapse border text-[11px]" {...props} />
      </div>
    ),
    th: ({ ...props }) => (
      <th
        className="border px-2 py-1 text-left text-[11px] font-semibold bg-muted [&[align=center]]:text-center [&[align=right]]:text-right"
        {...props}
      />
    ),
    td: ({ ...props }) => (
      <td
        className="border px-2 py-1 text-[11px] [&[align=center]]:text-center [&[align=right]]:text-right"
        {...props}
      />
    ),
    tr: ({ ...props }) => <tr className="even:bg-muted/50" {...props} />,
    code: ({ className, children, ...props }) => (
      <code
        className={`rounded bg-muted px-1 py-px font-mono text-[11px] ${className ?? ""}`}
        {...props}
      >
        {children}
      </code>
    ),
    pre: ({ children }) => (
      <pre className="my-2 overflow-x-auto rounded bg-muted/60 px-3 py-2 text-[11px] font-mono leading-5 whitespace-pre-wrap break-all">
        {children}
      </pre>
    ),
    blockquote: ({ children, node }: any) => {
      // Use the raw hast node to reliably read [!NOTE] etc. from first paragraph
      const firstPara = (node?.children ?? []).find(
        (c: any) => c.type === "element" && c.tagName === "p",
      );
      const rawText = hastText(firstPara).trim();
      const m = rawText.match(/^\[!(NOTE|TIP|WARNING|CAUTION)\](?:\s+(.+))?$/i);
      if (m) {
        const alertType = m[1].toUpperCase();
        const alertTitle = m[2]?.trim();
        const restChildren = React.Children.toArray(children)
          .filter((c) => typeof c !== "string" || c.trim() !== "")
          .slice(1);
        const typeMap: Record<string, "info" | "tip" | "warning" | "danger"> = {
          NOTE: "info",
          TIP: "tip",
          WARNING: "warning",
          CAUTION: "danger",
        };
        return (
          <Callout type={typeMap[alertType]} title={alertTitle}>
            {restChildren}
          </Callout>
        );
      }
      return (
        <blockquote className="my-2 border-l-2 border-muted-foreground/30 pl-3 italic text-muted-foreground text-xs">
          {children}
        </blockquote>
      );
    },
    a: ({ href, children, ...props }) => {
      if (href?.startsWith("/docs/") && onPageClick) {
        const slug = href.replace("/docs/", "");
        return (
          <button
            onClick={(e) => {
              e.preventDefault();
              onPageClick(slug);
            }}
            className="font-medium text-primary underline underline-offset-2 hover:text-primary/80 cursor-pointer"
            {...(props as any)}
          >
            {children}
          </button>
        );
      }
      return (
        <a
          className="text-primary underline underline-offset-2 hover:text-primary/80"
          href={href}
          {...props}
        >
          {children}
        </a>
      );
    },
    // img is handled via p detection above for block-level; this handles inline imgs
    img: ({ src, alt }: any) => (
      <img
        src={
          src?.startsWith("/") || src?.startsWith("http")
            ? src
            : `${process.env.BUN_PUBLIC_API_URL ?? ""}/static/${src}`
        }
        alt={alt ?? ""}
        className="max-w-full h-auto rounded"
      />
    ),
  };
}
